import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Shield, Camera, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const BiometricPolicyPage = () => {
  const { isAuthenticated } = useAuth();
  const homeRoute = isAuthenticated ? '/dashboard' : '/';
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Link>
          <Link to={homeRoute} className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">VirtualFit</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" data-testid="biometric-policy-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Biometric Data Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: May 29, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Overview</h2>
            </div>
            <p className="text-gray-600">
              VirtualFit uses artificial intelligence to generate virtual try-on images. This process involves analyzing photographs that you voluntarily upload, which may include facial features and body measurements. This Biometric Data Policy explains how we collect, use, store, and protect this data in compliance with applicable biometric privacy laws, including the Illinois Biometric Information Privacy Act (BIPA), the Texas Capture or Use of Biometric Identifier Act, the Washington Biometric Identifier law, and the EU General Data Protection Regulation (GDPR).
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Camera className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">What Biometric Data We Collect</h2>
            </div>
            <p className="text-gray-600">When you upload a photo for virtual try-on, our AI may process:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Facial geometry</strong> — to accurately position clothing on your image</li>
              <li><strong>Body proportions</strong> — to scale and fit clothing realistically</li>
              <li><strong>Skin tone data</strong> — to blend generated images naturally</li>
            </ul>
            <p className="text-gray-600 mt-3">
              We do <strong>not</strong> collect fingerprints, retina scans, voiceprints, or DNA. All biometric data is derived solely from photographs you voluntarily upload.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">How We Use Biometric Data</h2>
            <p className="text-gray-600">Your biometric data is used exclusively for:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Generating virtual try-on images you request</li>
            </ul>
            <p className="text-gray-600 mt-3">
              We <strong>never</strong> sell, lease, trade, or otherwise profit from your biometric data. We do not use it for advertising, surveillance, or identification purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Processors of Biometric Data</h2>
            <p className="text-gray-600">
              In accordance with BIPA Section 15(d) and GDPR Article 28, we disclose the following third-party 
              service providers who may receive or process biometric data on our behalf:
            </p>
            <div className="mt-4 space-y-3">
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-gray-700 text-sm">
                  <strong>Google LLC (Gemini AI API)</strong> — Processes user-uploaded photographs containing facial 
                  geometry and body measurements to generate virtual try-on images. Google acts as a data processor 
                  under our Data Processing Agreement (DPA) pursuant to GDPR Article 28. Photos are transmitted to 
                  Google Gemini API via encrypted API calls (TLS 1.3) solely to generate your virtual try-on image. 
                  Processing is governed by Google's API Terms of Service and applicable Data Processing Addendum. 
                  We do not knowingly permit Google to use your photos for model training or any purpose beyond 
                  fulfilling the API request. Google's data processing terms are available at{' '}
                  <a href="https://cloud.google.com/terms/data-processing-addendum" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline">
                    Google Cloud Data Processing Addendum
                  </a>.
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>Cloudinary Ltd.</strong> — Stores user-uploaded photographs (which may contain biometric data) 
                  in portfolio features. Cloudinary acts as a data processor under our DPA pursuant to GDPR Article 28. 
                  Images are encrypted at rest and in transit. Cloudinary does not access, analyze, or process the 
                  biometric content of stored images.
                </p>
              </div>
            </div>
            <p className="text-gray-600 mt-4">
              <strong>No other third parties</strong> receive, access, or process your biometric data. We do not 
              disclose biometric data to advertisers, data brokers, or any entity not listed above.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Consent</h2>
            <p className="text-gray-600">
              In accordance with the Illinois Biometric Information Privacy Act (BIPA) and GDPR, VirtualFit obtains 
              your <strong>explicit, informed, standalone consent</strong> before collecting or processing any biometric data.
            </p>
            <div className="mt-4 p-4 bg-rose-50 rounded-xl border border-rose-200">
              <p className="text-gray-700 text-sm">
                <strong>How consent is obtained:</strong> Before your first photo upload (whether as a free trial user 
                or registered user), you will be presented with a mandatory biometric consent checkbox. This checkbox 
                requires you to actively click to confirm:{' '}
                <em>"I am at least 18 years old and I explicitly consent for VirtualFit to process my biometric data 
                (facial geometry and body measurements) for the purpose of generating my virtual try-on image, as 
                described in the Privacy Policy and Biometric Data Policy. I understand that my biometric data will 
                be processed in real-time and will not be stored beyond this session unless I create an account."</em>
                {' '}You cannot proceed with your photo upload without providing this consent.
              </p>
              <p className="text-gray-700 text-sm mt-3">
                This consent requirement applies equally to free trial users and registered users. Free trial users 
                must check the biometric consent box before generating any try-on image.
              </p>
            </div>
            <p className="text-gray-600 mt-4">
              You may withdraw consent at any time by deleting your account, deleting your uploaded photos, or 
              contacting us at{' '}
              <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a>. 
              Withdrawal of consent will result in the immediate cessation of biometric data processing and the 
              deletion of any stored biometric-related data within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Storage & Security</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Uploaded photos are processed in real-time and are <strong>not permanently stored</strong> on our servers after the try-on image is generated, unless you explicitly save them to your portfolio.</li>
              <li>Biometric identifiers extracted during processing are ephemeral and discarded immediately after the try-on image is rendered.</li>
              <li>All data transmission uses 256-bit TLS encryption.</li>
              <li>Stored images (portfolios) are encrypted at rest using industry-standard encryption.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Data Retention & Destruction Schedule</h2>
            </div>
            <p className="text-gray-600">
              In compliance with BIPA Section 15(a), we maintain the following retention and destruction schedule:
            </p>
            <div className="mt-4 p-4 bg-rose-50 rounded-xl border border-rose-200">
              <p className="text-gray-700 text-sm font-medium">
                Biometric identifiers and biometric information shall be permanently destroyed no later than 
                the earlier of: (a) 30 days after account deletion or the purpose for which the data was collected 
                has been satisfied, or (b) three (3) years from the date of collection — whichever comes first.
              </p>
            </div>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
              <li><strong>Ephemeral processing data:</strong> Biometric identifiers extracted during AI try-on processing are discarded immediately and automatically after the try-on image is rendered. They are never written to persistent storage.</li>
              <li><strong>Free trial users:</strong> Uploaded photos and generated images are automatically deleted within 24 hours of the session ending.</li>
              <li><strong>Registered users:</strong> Portfolio images are retained for the duration of your active subscription. Upon account deletion or subscription cancellation, all associated biometric data and images are permanently destroyed within 30 days.</li>
              <li><strong>Maximum retention:</strong> Regardless of account status, no biometric data is retained for longer than three (3) years from the date of collection.</li>
              <li><strong>Destruction method:</strong> Data destruction is performed via secure deletion (overwrite and purge) from all primary storage and backup systems.</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You may request deletion of specific images or your entire account at any time via Settings or by 
              emailing <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a>. 
              Deletion requests are processed within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h2>
            <p className="text-gray-600">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Know what biometric data we hold about you</li>
              <li>Request deletion of your biometric data</li>
              <li>Withdraw consent to biometric data processing</li>
              <li>Obtain a copy of your biometric data in a portable format (GDPR)</li>
              <li>File a complaint with your local data protection authority</li>
              <li>Bring a private right of action for violations of BIPA (Illinois residents)</li>
            </ul>
            <p className="text-gray-600 mt-3">
              To exercise any of these rights, contact us at <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a>. 
              We will respond to verified requests within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Breach Notification</h2>
            <p className="text-gray-600">
              In the event of a data breach involving biometric data, VirtualFit is committed to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-3">
              <li><strong>GDPR (EU/EEA users):</strong> Notifying the relevant supervisory authority within <strong>72 hours</strong> of becoming aware of the breach, and notifying affected individuals without undue delay if the breach poses a high risk to their rights and freedoms.</li>
              <li><strong>US state laws:</strong> Notifying affected individuals as required by applicable state breach notification laws, including but not limited to Illinois, California, Texas, and Washington.</li>
              <li><strong>All users:</strong> Providing clear, plain-language notification via email describing the nature of the breach, the data affected, the measures taken, and recommended protective actions.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Minors & Age Restrictions</h2>
            </div>
            <p className="text-gray-600">
              VirtualFit is not intended for use by minors. We enforce the following age restrictions:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-3">
              <li><strong>United States (COPPA):</strong> VirtualFit does not knowingly collect personal information or biometric data from children under 13 years of age, in compliance with the Children's Online Privacy Protection Act (COPPA). If we learn that a child under 13 has provided us with personal or biometric data, we will promptly delete it.</li>
              <li><strong>EU/EEA (GDPR Article 8):</strong> Users in the EU/EEA must be at least 18 years of age to use VirtualFit and consent to biometric data processing.</li>
              <li><strong>General policy:</strong> Users must be at least 18 years of age to use VirtualFit, create an account, and consent to biometric data processing. No parental or guardian consent exception is permitted.</li>
            </ul>
            <p className="text-gray-600 mt-3">
              If you believe a minor has submitted biometric data through our platform, please contact us immediately 
              at <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> and 
              we will delete it within 24 hours.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Biometric Data Policy from time to time. In accordance with BIPA requirements, 
              we will provide at least <strong>30 days' advance written notice</strong> via email to registered users 
              before any material changes to how we collect, use, store, or share biometric data take effect. 
              Non-material updates (formatting, clarifications) will be posted on this page with an updated 
              "Last updated" date. Your continued use of VirtualFit after the effective date of material changes 
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about this Biometric Data Policy, contact us at:
            </p>
            <p className="text-gray-600 mt-2">
              <strong>VirtualFit</strong><br />
              Email: <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a><br />
              Website: <a href="https://virtualfit.work" className="text-rose-500 hover:underline">virtualfit.work</a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BiometricPolicyPage;
